// src/components/Upload.js
import React, { useState } from 'react';
import ReactGA from 'react-ga4';
import JSZip from 'jszip';
import axios from 'axios';
import "../Styles/Upload.css";

function Upload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState('');
  const [downloadLink, setDownloadLink] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const MAX_DATA_SIZE = 40 * 1024 * 1024; // 40 MB

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const isValidImage = (filename) =>
    /\.(jpe?g|png|webp)$/i.test(filename);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsProcessing(true);
    
    // Google Analytics event tracking
    ReactGA.event({
      category: 'User',
      action: 'Clicked Submit Button',
      label: 'Upload ZIP Attempt',
    });

    try {
      const zip = await JSZip.loadAsync(selectedFile);
      const files = Object.entries(zip.files);
      const formData = new FormData();

      let postsJson = null;
      const imageEntries = [];
      let totalSize = 0;

      for (const [name, file] of files) {
        if (file.dir) continue;

        if (name.endsWith('posts.json')) {
          postsJson = await file.async('blob');
        } else if (isValidImage(name)) {
          const blob = await file.async('blob');
          if (totalSize + blob.size > MAX_DATA_SIZE) continue;

          imageEntries.push({ name, blob });
          totalSize += blob.size;
        }
      }

      if (!postsJson) {
        setMessage('Missing posts.json file.');
        setIsProcessing(false);
        return;
      }

      formData.append('posts_json', postsJson, 'posts.json');
      imageEntries.forEach(({ name, blob }) => {
        formData.append(`img_${name}`, blob, name);
      });

      console.log(`Uploading ${imageEntries.length} images to backend...`);
      const sizeMB = (totalSize / (1024 * 1024)).toFixed(2);
      console.log(`Total upload size: ${sizeMB} MB`);

      const backendBaseURL = 'https://bereal-timelapse-backend.onrender.com'; // 👈 HTTPS
      const res = await axios.post(`${backendBaseURL}/process`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });

      if (res.data.download_url) {
        setDownloadLink(res.data.download_url);
        setIsSuccess(true);
        setMessage('Timelapse ready!');
      } else {
        setIsSuccess(false);
        setMessage('Processing failed. Invalid server response.');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setMessage('Our apologies, due to unexpected demand, we are working on upgrading our servers. Leave your email to be notified when we are back online.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    try {
      const backendBaseURL = 'https://bereal-timelapse-backend.onrender.com';
      const res = await axios.get(`${backendBaseURL}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'timelapse.mp4';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
      setMessage('Download error. Try again.');
    }
  };

  const handleShowWaitlistForm = () => {
    if (window.Tally) {
      window.Tally.openPopup('mVqZMJ', {
        layout: 'modal',
        width: 600,
        autoClose: true,
      });
    } else {
      alert('Form failed to load. Try again later.');
    }
  };

  return (
    <div className="upload">
      <form onSubmit={handleSubmit} className="upload-form">
        <input
          type="file"
          accept=".zip"
          onChange={handleFileChange}
          disabled={isProcessing}
          className="upload-button"
        />
        <button type="submit" className="submit-button" disabled={isProcessing}>
          {isProcessing ? <div className="spinner" /> : 'Create Timelapse'}
        </button>
       {message && (
        <div className={`upload-message ${isProcessing ? 'info' : isSuccess ? 'success' : 'error'}`}>
          {message}
          {!isProcessing && !isSuccess && (
            <div style={{ marginTop: '1em' }}>
              <button type="button" onClick={handleShowWaitlistForm} className="waitlist-button">
                Click to be notified!
              </button>
            </div>
          )}
        </div>
       )} 
      </form>
      {downloadLink && (
        <button onClick={handleDownload} className="download-button">
          Download Video
        </button>
      )}
    </div>
  );
}

export default Upload;
